"use client";

import Button from "@marginly/ui/components/button";
import T from "@marginly/ui/components/typography";
import NextLink from "next/link";
import { useAccount } from "wagmi";

import { useMarginlyApi } from "@/hooks/api";

import { numberFormatter } from "../../util/formatters";
import Space from "../common/space";
import Footer from "./footer/footer";
import { Header } from "./header";
import HowTo from "./how-to/how-to";
import PreciousBlock from "./precious/precious";
import { ShareLink } from "./share-link";
import {
  AirdropWrapper,
  Container,
  OpeningBlock,
  OpeningDecor,
  OpeningTitle,
  PointCard,
  PointSystem,
  PointSystemGrid,
  SparkinfoGrid,
  SparkinfoMedia,
  SparkinfoWrapper,
} from "./styled";
import TimingSchema from "./timing-schema/timing-schema";

const br = <br className="hide-mobile" />;

const showBonus = (bonus?: number) =>
  bonus === 1 || !bonus ? undefined : bonus;

const Sparks = () => {
  document.documentElement.setAttribute("data-theme", "dark");

  const account = useAccount();

  const { data: userPoints } = useMarginlyApi({
    enabled: Boolean(account.address),
    endpoint: "userPoints",
    args: [account.address],
  });

  // const { data: leaderboardPoints } = useMarginlyApi({
  //   endpoint: "leaderboardPoints",
  //   args: [50],
  // });

  const pointsDetailsList = Object.entries(userPoints?.detailsByPool || []).map(
    ([_, value]) => value,
  );

  const loyalUserBonus = Math.max(
    ...pointsDetailsList.map((pool) => pool.loyalUserBonus),
  );
  const emailBonus = Math.max(
    ...pointsDetailsList.map((pool) => pool.emailBonus),
  );
  const bootstrapBonus = Math.max(
    ...pointsDetailsList.map((pool) => pool.bootstrapBonus),
  );

  const showLoyalUserBonus = showBonus(loyalUserBonus);
  const showEmailBonus = showBonus(emailBonus);
  const showBootstrapBonus = showBonus(bootstrapBonus);

  const pointsSum = pointsDetailsList.reduce(
    (acc, curr) => ({
      ...acc,
      liquidityPoints: acc.liquidityPoints + curr.liquidityPoints,
      referralPoints: acc.referralPoints + curr.referralPoints,
      topReferralPoints: acc.topReferralPoints + curr.topReferralPoints,
      pointsPerDay: acc.pointsPerDay + curr.pointsPerDay,
    }),
    {
      liquidityPoints: 0,
      referralPoints: 0,
      topReferralPoints: 0,
      pointsPerDay: 0,
    },
  );
  const { liquidityPoints, pointsPerDay, referralPoints, topReferralPoints } =
    pointsSum;
  const hasSparks = Boolean(userPoints?.balance);
  const hasLiquidityPoints = Boolean(liquidityPoints);
  const hasReferralPoints = Boolean(referralPoints);
  const hasTopReferralPoints = Boolean(topReferralPoints);

  const pointGridColumns =
    Number(hasLiquidityPoints) +
    Number(hasReferralPoints) +
    Number(hasTopReferralPoints);

  return (
    <div>
      <Header />
      <AirdropWrapper>
        <div id="top" />

        <Space height={192} heightMobile={96} />

        <Container className="new">
          <section>
            <OpeningBlock>
              {hasSparks ? (
                <OpeningTitle>
                  You have {br}
                  {numberFormatter(Math.ceil(userPoints?.balance || 0))} sparks
                </OpeningTitle>
              ) : (
                <OpeningTitle>
                  Points that Spark {br}in your wallet
                </OpeningTitle>
              )}

              <Space height={64} heightMobile={128} />

              {hasSparks ? (
                <PointSystem>
                  <T headerL>Your Sparks in&nbsp;detail</T>
                  <Space height={12} heightMobile={4} />
                  <T body secondary>
                    Earning {numberFormatter(Math.ceil(pointsPerDay))} Sparks
                    per day
                  </T>
                  <Space height={34} heightMobile={24} />

                  <SparkinfoWrapper>
                    <SparkinfoGrid>
                      {showLoyalUserBonus && (
                        <SparkinfoMedia>
                          <div className="thumb">
                            <img src="/images/airdrop/heart.png" alt="" />
                          </div>
                          <div>
                            <T caption secondary>
                              Loyalty bonus
                            </T>
                            <T body>{loyalUserBonus}&times;</T>
                          </div>
                        </SparkinfoMedia>
                      )}

                      {showEmailBonus && (
                        <SparkinfoMedia>
                          <div className="thumb">
                            <img src="/images/airdrop/email.png" alt="" />
                          </div>
                          <div>
                            <T caption secondary>
                              Early adapter bonus
                            </T>
                            <T body>{emailBonus}&times;</T>
                          </div>
                        </SparkinfoMedia>
                      )}

                      {showBootstrapBonus && (
                        <SparkinfoMedia>
                          <div className="thumb">
                            <img src="/images/airdrop/deposit.png" alt="" />
                          </div>
                          <div>
                            <T caption secondary>
                              Bootsrtrap event bonus
                            </T>
                            <T body>{bootstrapBonus}&times;</T>
                          </div>
                        </SparkinfoMedia>
                      )}
                    </SparkinfoGrid>
                  </SparkinfoWrapper>

                  <Space height={32} heightMobile={24} />

                  <PointSystemGrid columns={pointGridColumns}>
                    {hasLiquidityPoints && (
                      <PointCard className="spark-info">
                        <div>
                          <T body>For depositing liquidity</T>
                          <Space height={0} heightMobile={2} />
                          <T headerM>
                            {numberFormatter(Math.ceil(liquidityPoints))}
                          </T>
                        </div>

                        <img src="/images/airdrop/deposit-2.png" alt="" />
                      </PointCard>
                    )}
                    {hasReferralPoints && (
                      <PointCard className="spark-info">
                        <div>
                          <T body>10% of referrals</T>
                          <Space height={0} heightMobile={2} />
                          <T headerM>
                            {numberFormatter(Math.ceil(referralPoints))}
                          </T>
                        </div>

                        <img src="/images/airdrop/user.png" alt="" />
                      </PointCard>
                    )}
                    {hasTopReferralPoints && (
                      <PointCard className="spark-info">
                        <div>
                          <T body>5% referrals of referrals</T>
                          <Space height={0} heightMobile={2} />
                          <T headerM>
                            {numberFormatter(Math.ceil(topReferralPoints))}
                          </T>
                        </div>

                        <img src="/images/airdrop/users-green.png" alt="" />
                      </PointCard>
                    )}
                  </PointSystemGrid>
                </PointSystem>
              ) : (
                <PointSystem>
                  <T headerL>
                    A Spark is a unit of reward {br}in the Marginly point system
                  </T>
                  <Space height={40} heightMobile={40} />
                  <PointSystemGrid columns={3}>
                    <PointCard>
                      <div>
                        <T headerS>Rewarding LPs</T>
                        <Space height={2} heightMobile={2} />
                        <T body secondary>
                          Deposits in Marginly pools earn you Sparks
                        </T>
                      </div>

                      <img src="/images/airdrop/deposit-2.png" alt="" />
                    </PointCard>
                    <PointCard>
                      <div>
                        <T headerS>Rewarding users</T>
                        <Space height={2} heightMobile={2} />
                        <T body secondary>
                          Marginly allocates Sparks for user actions
                        </T>
                      </div>

                      <img src="/images/airdrop/user.png" alt="" />
                    </PointCard>
                    <PointCard>
                      <div>
                        <T headerS>For the Airdrop</T>
                        <Space height={2} heightMobile={2} />
                        <T body secondary>
                          Each Spark gets you a chunk of the future Airdrop
                        </T>
                      </div>

                      <img
                        src="/images/airdrop/marginly-point-on-dark.png"
                        alt=""
                      />
                    </PointCard>
                  </PointSystemGrid>
                </PointSystem>
              )}

              <OpeningDecor src="/images/airdrop/bg.png" alt="" />
            </OpeningBlock>
          </section>

          <Space height={192} heightMobile={96} />

          {hasSparks ? (
            <div>
              {/* <Leaderboard points={leaderboardPoints} />
              <Space height={192} heightMobile={96} /> */}
              <TimingSchema />
              <Space height={196} heightMobile={96} />
            </div>
          ) : (
            <div>
              <TimingSchema />
              <Space height={196} heightMobile={96} />
              <PreciousBlock />
              <Space height={192} heightMobile={96} />
              {/* <Space height={192} heightMobile={96} />
              <Leaderboard points={leaderboardPoints} /> */}
            </div>
          )}
          <HowTo />
          <Space height={64} heightMobile={32} />
          <ShareLink />
          <Space height={192} heightMobile={96} />
        </Container>
        <Footer />
      </AirdropWrapper>
    </div>
  );
};

export default Sparks;
